// Single source of truth for the backend base URL + a small fetch helper.
//
// Override per-environment with REACT_APP_API_BASE_URL:
//   • local dev  → .env.local
//   • otherwise  → production below
export const API_URL =
  process.env.REACT_APP_API_BASE_URL || 'https://dev.api.outfino.io/v3';

// `react-scripts build` forces NODE_ENV='production' for every build, including
// the one deployed to the dev host — so NODE_ENV cannot tell dev from prod. The
// API base URL is the real per-environment signal (build:prod → api.outfino.io,
// build:dev → dev.api.outfino.io, npm start → whatever .env.local names), which
// is also how the admin panel decides. See components/DevBanner.js.
export const PROD_API = 'https://api.outfino.io/v3';
export const IS_PROD = API_URL === PROD_API;

const TOKEN_KEY = 'creator_token';

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}
export function setToken(t) {
  localStorage.setItem(TOKEN_KEY, t);
}
export function clearToken() {
  localStorage.removeItem(TOKEN_KEY);
}

/// The API validates the raw Authorization header (NO "Bearer" prefix), mirroring
/// the admin panel + mobile app.
function authHeaders(extra) {
  const token = getToken();
  return {
    ...(token ? { Authorization: token } : {}),
    ...(extra || {}),
  };
}

/// Thin JSON fetch wrapper. Unwraps the API's `{ message: {...} }` envelope and
/// the brand controllers' `{ success, data }` shape. Throws on non-2xx with a
/// `.status` so callers can special-case 401/403.
export async function api(path, { method = 'GET', body, headers, raw } = {}) {
  const res = await fetch(`${API_URL}${path}`, {
    method,
    headers: authHeaders(
      raw ? headers : { 'Content-Type': 'application/json', ...(headers || {}) }
    ),
    body: raw ? body : body != null ? JSON.stringify(body) : undefined,
  });

  let data = null;
  try {
    data = await res.json();
  } catch {
    // no/invalid JSON body
  }

  if (!res.ok) {
    const err = new Error(data?.message || data?.error || `HTTP ${res.status}`);
    err.status = res.status;
    err.data = data;
    throw err;
  }

  // Unwrap common envelopes.
  if (data && typeof data === 'object') {
    if ('data' in data && 'success' in data) return data.data;
    if ('message' in data) return data.message;
  }
  return data;
}
